import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom'
import { AuthProvider } from '@/auth/AuthContext'
import { RequireAuth } from '@/auth/RequireAuth'
import { AppShell } from '@/components/shell/AppShell'
import { ToastProvider } from '@/components/ui'
import LoginPage from '@/pages/LoginPage'
import InvitePage from '@/pages/auth/InvitePage'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage'
import DashboardPage from '@/pages/DashboardPage'
import TenantsListPage from '@/pages/tenants/TenantsListPage'
import NewTenantPage from '@/pages/tenants/NewTenantPage'
import TenantLayout from '@/pages/tenants/TenantLayout'
import OverviewTab from '@/pages/tenants/tabs/OverviewTab'
import SettingsTab from '@/pages/tenants/tabs/SettingsTab'
import DomainsTab from '@/pages/tenants/tabs/DomainsTab'
import PlanTab from '@/pages/tenants/tabs/PlanTab'
import AddonsTab from '@/pages/tenants/tabs/AddonsTab'
import SportsTab from '@/pages/tenants/tabs/SportsTab'
import FeesTab from '@/pages/tenants/tabs/FeesTab'
import PeopleTab from '@/pages/tenants/tabs/PeopleTab'
import ActivityTab from '@/pages/tenants/tabs/ActivityTab'
import DangerZoneTab from '@/pages/tenants/tabs/DangerZoneTab'
import UsersPage from '@/pages/UsersPage'
import UserDetailPage from '@/pages/users/UserDetailPage'
import AuditLayout from '@/pages/audit/AuditLayout'
import ChangesTab from '@/pages/audit/ChangesTab'
import LoginsTab from '@/pages/audit/LoginsTab'
import NotFoundPage from '@/pages/NotFoundPage'

/**
 * Every route in the console. Exported separately from <App> so tests can
 * mount it inside a MemoryRouter.
 *
 *   /login
 *   /invite/:token           accept a super-admin invite (signed out; token stays in the path)
 *   /forgot-password         ask for a reset link (signed out)
 *   /reset-password          set a new password (signed out; #<token>, cleared on load)
 *   /dashboard
 *   /tenants                 ?q=&status=&sort=&page=
 *   /tenants/new
 *   /tenants/:id/:tab        overview | settings | domains | plan | add-ons |
 *                            sports | fees | people | activity | danger-zone
 *   /users                   ?q=&kind=&page=
 *   /users/:id
 *   /audit/changes | /audit/logins
 *
 * Old portal paths (/leagues, /leagues/:id, /logins, /dev-docs,
 * /architecture) redirect to their replacements.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="tenants" element={<TenantsListPage />} />
        <Route path="tenants/new" element={<NewTenantPage />} />
        <Route path="tenants/:id" element={<TenantLayout />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<OverviewTab />} />
          <Route path="settings" element={<SettingsTab />} />
          <Route path="domains" element={<DomainsTab />} />
          <Route path="plan" element={<PlanTab />} />
          <Route path="add-ons" element={<AddonsTab />} />
          <Route path="sports" element={<SportsTab />} />
          <Route path="fees" element={<FeesTab />} />
          <Route path="people" element={<PeopleTab />} />
          <Route path="activity" element={<ActivityTab />} />
          <Route path="danger-zone" element={<DangerZoneTab />} />
          <Route path="*" element={<TenantOverviewRedirect />} />
        </Route>
        <Route path="users" element={<UsersPage />} />
        <Route path="users/:id" element={<UserDetailPage />} />
        <Route path="audit" element={<AuditLayout />}>
          <Route index element={<Navigate to="changes" replace />} />
          <Route path="changes" element={<ChangesTab />} />
          <Route path="logins" element={<LoginsTab />} />
          <Route path="*" element={<Navigate to="/audit/changes" replace />} />
        </Route>

        {/* Old portal URLs */}
        <Route path="leagues" element={<Navigate to="/tenants" replace />} />
        <Route path="leagues/:id" element={<TenantOverviewRedirect />} />
        <Route path="logins" element={<Navigate to="/audit/logins" replace />} />
        <Route path="dev-docs" element={<Navigate to="/dashboard" replace />} />
        <Route path="architecture" element={<Navigate to="/dashboard" replace />} />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  )
}

/** Unknown tenant tab, or an old /leagues/:id link → the tenant's Overview. */
function TenantOverviewRedirect() {
  const { id } = useParams()
  return <Navigate to={`/tenants/${id}/overview`} replace />
}

/** Opt in to React Router v7 behaviour now (silences the upgrade warnings). */
export const ROUTER_FUTURE = { v7_startTransition: true, v7_relativeSplatPath: true } as const

export default function App() {
  return (
    <BrowserRouter future={ROUTER_FUTURE}>
      <ToastProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  )
}
